import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

import Papa from 'papaparse';
import { getMoleculeFamily } from '../utils/getMoleculeFamily';

import Fuse from 'fuse.js'

let cache = null

countries.registerLocale(enLocale);

export async function loadProducts() {
    if (cache) return cache
    const res = await fetch(`${import.meta.env.BASE_URL}Catalogue.csv`)
    const buffer = await res.arrayBuffer()
    const csv = new TextDecoder("utf-8").decode(buffer)
    const { data } = Papa.parse(csv, {
        delimiter: ";",
        header: true,
        skipEmptyLines: true,
        quoteChar: '"',
        newline: "\r\n",
    })

    const parsed = data.map(row => {
        const { name, purity, method } = parseNom(row["Nom"])
        row["NomClean"] = name
        row["Purity"] = purity
        row["Method"] = method
        return row
    })

    const grouped = {}
    parsed.forEach(row => {
        const ref = String(row["Réf EPSILON"])
        if (!ref) return
        if (!grouped[ref] && row["NomPourTri"]) grouped[ref] = row
    })

    cache = Object.values(grouped)
    return cache
}

// applique les tries et filtres
export function filterAndSort(products, {search, selectedFamily, sortOrder}) {
    let result = products
    return result
        // par famille à gauche
        .filter(p => selectedFamily === "All" || getMoleculeFamily(p) === selectedFamily)
        // par tri en haut
        .sort((a,b) => {
            switch(sortOrder) {
                case "nameAsc" :    return a["NomPourTri"].localeCompare(b["NomPourTri"]);
                case "nameDesc" :   return b["NomPourTri"].localeCompare(a["NomPourTri"]);
                case "casAsc":      return a["CAS"].localeCompare(b["CAS"]);
                case "casDesc":     return b["CAS"].localeCompare(a["CAS"]);
                case "purityAsc": {
                    
                    const pA = Number.parseFloat(a["Purity"]);
                    const pB = Number.parseFloat(b["Purity"]);
                    if (Number.isNaN(pA)) return 1;   // a va en bas
                    if (Number.isNaN(pB)) return -1;  // b va en bas
                    return pB - pA;
                }
                case "purityDesc": {
                    const pA = Number.parseFloat(a["Purity"]);
                    const pB = Number.parseFloat(b["Purity"]);
                    if (Number.isNaN(pA)) return 1;   // a va en bas
                    if (Number.isNaN(pB)) return -1;  // b va en bas
                    return pA - pB;
                    
                }default: return 0;
            }
    });
}

export function searchProducts(products, search) {
    if(!search) return products;
    const cleanSearch = search.replace(/^EC-/i, "").trim()
    const fuse = new Fuse(products, {
        keys: [
            { name: "NomClean", getFn: p => String(p["NomClean"] ?? "") },
            { name: "CAS", getFn: p => String(p["CAS"] ?? "") },
            { name: "NomPourTri", getFn: p => String(p["NomPourTri"] ?? "") },
            { name: "Réf EPSILON", getFn: p => String(p["Réf EPSILON"] ?? "") },
        ],
        threshold: 0.3,
        includeScore: true,
        shouldSort: true,
    });
    return fuse.search(cleanSearch)
        .sort((a, b) => a.score - b.score) // score le plus bas = meilleur match, en premier
        .map(r => r.item);
}

// compte le nombre d'élement de chaque 
export function countByFamily(products) {
    const families = ["Phosphonic Acids", "Phosphonates", "Phosphonium Salts", "Phosphoranes", "Phosphines", "Chemical Intermediates"]
    const counts = { "All" : products.length.toLocaleString('en-US') }
    families.forEach(f => {
        counts[f] = products.filter(p => getMoleculeFamily(p) === f).length
    })
    return counts
}

// Récupéré les infos sur un seul produit
export function getProductById(products, id) {
    return products.find(p => String(p["Réf EPSILON"]) === String(id))
}

// Formater la formule chimique de la molécule
export function formatFormula(formula) {
    if(!formula) return ""
    return formula
        .replace(/([A-Z][a-z]?)/g, match => `<span>${match}</span>`)
        .replace(/(\d+)/g, match => `<sub>${match}</sub>`)
}

export const getDefaultPurity = (purity) => {
    if(!purity) return "default";
    const num = Number.parseFloat(purity);

    if (num >= 99) return "99";
    if (num >= 98) return "min98";
    if (num >= 95) return "95";
    if (num >= 80) return "80-90";
    return "min50";
};

//***********************************************************************************//

export function parseNom(nom) {
    if (!nom) return { name: "", purity: "", method: "" };

    const match = nom.match(
        /(,\s*|\s+)(tech\.?\s*)?(min\.?\s*)?([\d.,]+-?[\d.]*\s*%?)(\s*min\.?)?\s*(\([^)]+\))?$/i
    );

    if (match) {
        const tech   = match[2] ? "tech. " : "";
        const min    = (match[3] || match[5]) ? "min. " : "";
        const value  = match[4].replace(",", ".").trim(); // "97,5" → "97.5"
        const method = match[6] ? match[6].replace(/[()]/g, "").trim() : "";

        return {
            name:   nom.slice(0, match.index).trim(),
            purity: (tech + min + value).trim(),
            method,
        };
    }

    return { name: nom, purity: "", method: "" };
}

//***********************************************************************************//

export const getCountryOptions = () =>
    Object.entries(countries.getNames("en", { select: "official" }))
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.name.localeCompare(b.name));